import { Alert, Button, ButtonRow, FormSection } from '@ksyos/design-system';
import { difference, orderBy } from 'lodash';
import { useEffect, useState } from 'react';
import type { IService } from '@common/interfaces/';
import { translateKey } from '@frontend/translate';
import { SortDirection } from '@frontend/utils';
import type { IServiceOrganizationConsumer } from '../../../../interfaces/serviceOrganizationConsumer';
import { ActionBarShowAndRemoveSelection } from '../../../../userDataManagement/ActionBarShowAndRemoveSelection';
import { findServiceConsumerOrganizations } from '../../../personalSettings/preferenceLists/personalSettingsPreferenceListHelper';
import { PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal } from '../../../personalSettings/preferenceLists/PreferenceListExamination/Modals/PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal';
import { PersonalSettingsPreferenceListExaminationConsumersTable } from '../../../personalSettings/preferenceLists/PreferenceListExamination/PreferenceListExaminationTables/PersonalSettingsPreferenceListExaminationConsumersTable';

interface IPreferredServiceOrganizationConsumersProps {
    hasError: boolean;
    onChange: (preferredServiceOrganizationConsumers: string[]) => void;
    onDoValidate: () => void;
    selectedConsumerOrganizationIds: string[];
    service: IService;
    title?: string;
}

export function PreferredServiceOrganizationConsumers(props: IPreferredServiceOrganizationConsumersProps) {
    const [showAddOrganizationNationWideModal, setShowAddOrganizationNationWideModal] = useState(false);
    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    const [serviceOrganizationConsumers, setServiceOrganizationConsumers] = useState<IServiceOrganizationConsumer[]>(
        []
    );
    const [selectedConsumerOrganizationIds, setSelectedConsumerOrganizationIds] = useState<string[]>([]);

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    async function addOrganizations(ids: string[]) {
        const newOrganizations = [...props.selectedConsumerOrganizationIds, ...ids];
        props.onChange(newOrganizations);
        props.onDoValidate();
        setShowAddOrganizationNationWideModal(false);
    }

    async function loadOrganizations() {
        if (props.selectedConsumerOrganizationIds.length > 0) {
            const result = await findServiceConsumerOrganizations({
                serviceId: props.service.id,
                organizationIds: props.selectedConsumerOrganizationIds,
                page: 1,
                limit: 100,
            });
            setServiceOrganizationConsumers(result.serviceOrganizationConsumers);
        } else {
            setServiceOrganizationConsumers([]);
        }
    }

    useEffect(() => {
        loadOrganizations();
    }, [props.selectedConsumerOrganizationIds.length]);

    useEffect(() => {
        sortPreferredConsumers();
    }, [sortKey, sortDirection]);

    async function handleDeleteSelectedPreferredOrganizations() {
        const newIds = difference(props.selectedConsumerOrganizationIds, selectedConsumerOrganizationIds);
        props.onChange(newIds);
        props.onDoValidate();
        setSelectedConsumerOrganizationIds([]);
    }

    async function handleClose() {
        setSelectedConsumerOrganizationIds([]);
    }

    function sortPreferredConsumers() {
        setServiceOrganizationConsumers((current) => orderBy(current, sortKey, sortDirection));
    }

    return (
        <FormSection title={props.title ? props.title : translateKey('carePath.requesterPreferenceList')}>
            {!props.hasError && props.selectedConsumerOrganizationIds.length === 0 && (
                <Alert appearance="info">
                    {translateKey('carePath.preferredServiceOrganizationConsumersRequiredMessage')}
                </Alert>
            )}

            {props.hasError && (
                <div className="has-danger">
                    <Alert appearance="warning">
                        {translateKey('carePath.preferredServiceOrganizationConsumersRequiredMessage')}
                    </Alert>
                </div>
            )}

            <ButtonRow className="mb-4">
                <Button
                    iconLeft="add"
                    onClick={() => setShowAddOrganizationNationWideModal(true)}
                >
                    {translateKey('organization.add')}
                </Button>
            </ButtonRow>

            {props.selectedConsumerOrganizationIds.length > 0 && (
                <PersonalSettingsPreferenceListExaminationConsumersTable
                    isReadOnly={false}
                    onChange={async (selected) => setSelectedConsumerOrganizationIds(selected)}
                    onSort={handleSort}
                    selectedServiceOrganizationConsumersOrganizationIds={selectedConsumerOrganizationIds}
                    serviceOrganizationConsumers={serviceOrganizationConsumers}
                    sortDirection={sortDirection}
                    sortKey={sortKey}
                />
            )}
            <ActionBarShowAndRemoveSelection
                handleClose={handleClose}
                handleDelete={handleDeleteSelectedPreferredOrganizations}
                isOpen={selectedConsumerOrganizationIds.length > 0}
                selectedIds={selectedConsumerOrganizationIds}
            />

            <PersonalSettingsPreferenceListExaminationServiceConsumerSearchNationWideModal
                isOpen={showAddOrganizationNationWideModal}
                onAddOrganizations={addOrganizations}
                onClose={async () => setShowAddOrganizationNationWideModal(false)}
                selectedServiceOrganizationConsumersOrganizationIds={serviceOrganizationConsumers.map(
                    (serviceOrganizationConsumer) => serviceOrganizationConsumer.organization.id
                )}
                serviceId={props.service.id}
            />
        </FormSection>
    );
}
